
import { PTC_TAG_TYPE, RAVEN_COMMON_KEYS, RAVEN_PAGE_NAME } from '@/analytics/raven/ravenConstants'
import {
    checkIfAnyAncilliarySelected,
    getInsuranceData
} from '@/components/itinerary/Addons/MealBaggage/AddonsHelperUtils'
import { STATSIG_SPLIT_RT_EXP_NAME } from '@/constants/experiment'
import { WOW_PRICE_CT_DT_EXPERIMENT_KEY } from '@/constants/flights'
import { PE_DOM_EXPERIMENT } from '@/constants/flights/premiumEconomy'
import { SRP_REVAMP_EXPERIMENTS } from '@/constants/flights/srpRevamp'
import { selectExperiment } from '@/redux/slices/statsig'
import { getHydratedStore } from '@/redux/store/configureStore'
import { SearchData } from '@/types/srp'
import { getBasePlusTax, isServer } from '@/utils'
import { isEmpty } from '@/utils/general/browserHelper'
import { getCookie } from '@/utils/general/cookie'
import { dayDiff, formatDateStandardized, getFormattedDate } from '@/utils/general/date'
import { isCfwTagPresent } from '@/utils/general/uiHelper'
import { isUserSignedIn } from '@/utils/general/user'
import { getPtcTagData } from '@/utils/srp/helper'
import { ProcessedData } from '@/utils/srp/types'
import { isStatsigClientInitialized, Statsig } from '@/utils/statsig'

import { Raven } from '.'

export const stringifyPayload = (payload: any) => {
    const keys = Object.keys(payload)
    keys.forEach(key => {
        if (
            key === 'a_fare_price' ||
            key === 'a_ct_discount' ||
            key === 'supercoin_balance' ||
            key === 'supercoin_earned' ||
            key === 'supercoin_burnt' ||
            key === 'wallet_balance_used' ||
            key === 'convenience_fee'
        )
            payload[key] = Number(payload[key])
        else {
            payload[key] = '' + payload[key]
            if (key === 'a_origin' || key === 'a_destination') {
                payload[key] = payload[key]?.toUpperCase()
            }else if(key === 'a_origin_city' || key === 'a_destination_city'){
                return;
            
            } else {
                payload[key] = payload[key]?.toLowerCase()
            }
        }
    })
    return payload
}

export const extraRavenPayload = (itineraryData: any) => {
    const {
        REWARDS = [],
        COUPON: { appliedCouponDetails = {} } = {},
        ANCILLARY = {},
        INSURANCES = {},
        CONTACT_INFORMATION:CONTACT_INFO = {}
    } = itineraryData || {}
    const rewardData = REWARDS?.filter((reward: any) => {
        return reward.rewardType === 'SUPERCOINS'
    })?.[0]?.rewardsData
    const insuranceData = getInsuranceData(INSURANCES)
    const extraPayload = {
        a_smb_booked:
            checkIfAnyAncilliarySelected(ANCILLARY, 'SEAT', insuranceData) ||
            checkIfAnyAncilliarySelected(ANCILLARY, 'MEAL', insuranceData) ||
            checkIfAnyAncilliarySelected(ANCILLARY, 'BAGGAGE', insuranceData)
                ? 'yes'
                : 'no',
        a_tb_insurance_booked: checkIfAnyAncilliarySelected(ANCILLARY, 'INSURANCE', insuranceData) ? 'yes' : 'no',
        ct_coupon_applied: appliedCouponDetails?.couponData?.[0]?.couponCode || '',
        supercoin_burnt: rewardData?.superCoinDetail?.balanceDetails?.redeemableSuperCoinAmount || 0,
        supercoin_earned: rewardData?.superCoinDetail?.earnConfigDetails?.maxEarnCoins || 0,
        country_code: (CONTACT_INFO?.phoneNumber?.mobileCountryCode?.startsWith('+') ? '' : '+') + (CONTACT_INFO?.phoneNumber?.mobileCountryCode || 'NA')
    }
    return extraPayload
}

export const superCoinPayload = (itineraryData: any) => {
    const { REWARDS = [] } = itineraryData || {}
    const rewardData = REWARDS?.filter((reward: any) => {
        return reward.rewardType === 'SUPERCOINS'
    })?.[0]?.rewardsData
    const payload = {
        supercoin_account_status: rewardData?.superCoinDetail?.status === 'SUCCESS' ? 'yes' : 'no',
        supercoin_balance: rewardData?.superCoinDetail?.balanceDetails?.superCoinBalance || 0,
        supercoin_burnt: rewardData?.superCoinDetail?.balanceDetails?.redeemableSuperCoinAmount || 0
    }
    return payload
}


/** Resolve sector for seat reco Statsig: DOM → seat_reco_dom, else intl → seat_reco_int */
const inferSectorTypeForSeatReco = (ravenPayload?: Record<string, unknown>) => {
    const rawTripType = ravenPayload?.a_trip_type
    if (rawTripType === undefined || rawTripType === null || rawTripType === '') return undefined
    const normalizedTripType = String(rawTripType).toLowerCase()
    if (normalizedTripType === 'domestic') return 'DOM'
    if (normalizedTripType === 'international') return 'INTL'
    return undefined
}

/** Same arm the SRP shows: _app resolves it server-side into Redux, cookie overrides included. */
const getSrpRevampVariant = ({ cookieName, experimentName }: (typeof SRP_REVAMP_EXPERIMENTS)['dom' | 'intl']) => {
    let reduxVariant = ''
    try {
        reduxVariant = selectExperiment(experimentName)(getHydratedStore().getState())
    } catch {
        reduxVariant = ''
    }
    return getCookie(cookieName) || reduxVariant || 'N/A'
}

export const getRavenStatsigPayload = (ravenPayload?: Record<string, unknown>) => {
    try {
        if (isServer()) return {}
        // Redux-backed, so these go out even before the Statsig client is ready.
        let payload: Record<string, unknown> = {
            dom_srp_revamp: getSrpRevampVariant(SRP_REVAMP_EXPERIMENTS.dom),
            intl_srp_revamp: getSrpRevampVariant(SRP_REVAMP_EXPERIMENTS.intl)
        }
        if (isStatsigClientInitialized()) {
            const sectorType = inferSectorTypeForSeatReco(ravenPayload)
            const seatRecoExperimentName =
                sectorType === 'DOM' ? 'seat_reco_dom' : sectorType ? 'seat_reco_int' : null
            const seat_recommendation_ab = seatRecoExperimentName
                ? Statsig.getExperiment(seatRecoExperimentName)?.get('variant', 'N/A')
                : 'N/A'
            payload = {
                ...payload,
                international_v7: Statsig.getExperiment(STATSIG_SPLIT_RT_EXP_NAME)?.get("variant", "N/A"),
                seat_recommendation_ab,
                // Intl PE is live for everyone, experiment will run only for domestic users.
                a_pe_exp_variant:
                    sectorType === 'INTL'
                        ? 'N/A'
                        : Statsig.getExperiment(PE_DOM_EXPERIMENT.experimentName)?.get(PE_DOM_EXPERIMENT.experimentParameter, 'N/A'),
                wow_ab_key: Statsig.getExperiment(WOW_PRICE_CT_DT_EXPERIMENT_KEY)?.get('variant', 'N/A')
            }
        }
        return payload
    } catch (error) {
        console.log(error)
        return {}
    }
}

export const ravenSDKTrigger = (eventName: string, ravenPayload: any) => {
    const commonPayload = {
        platform: 'desktop',
        login_status: isUserSignedIn() ? 'yes' : 'no',
        domain: window.location.host,
        vertical: 'air'
    }
    const statsigPayload = getRavenStatsigPayload(ravenPayload) || {}
    const newRavenPayload = stringifyPayload(ravenPayload)
    Raven.push({
        eventName: eventName,
        eventData: { ...newRavenPayload, ...commonPayload, ...statsigPayload }
    })
}

export const ravenSDKItinTrigger = (eventName: string, itineraryData: any, additionpayload: any) => {
    const {
        SEARCH_DETAILS = {},
        FLIGHT: { flights = {} } = {},
        FARE_CARDS: {  onwardFareCards: fareCards = {} } = {},
        BANNERS: { MULTI_LOYALTY = {} } = {},
        VISA_REJECTION_COVER = {}
    } = itineraryData || {}
    const flightNumbers: any[] = []
    const airlineNames: any[] = []
    let itineraryId = ''
    if (typeof window !== 'undefined') {
        const locationArray = window?.location?.pathname?.split('/') || []
        itineraryId = locationArray?.[locationArray?.length - 2] || ''
    }
    const { programDetails = [] } = MULTI_LOYALTY || {}
    if (!isEmpty(flights)) {
        flights.forEach((flight: { segments: any[] }) =>
            flight.segments.forEach(segment => {
                flightNumbers.push(segment.airline + '-' + segment.flightNumber)
                airlineNames.push(segment.airlineName)
            })
        )
        const selectedFareCard = fareCards?.filter?.((fareCard: any) => {
            return fareCard.selected === true
        })?.[0]
        const flightNumbersValue = flightNumbers.length > 1 ? flightNumbers.join(',') : flightNumbers[0]
        const airlineNameValue = airlineNames.length > 1 ? airlineNames.join(',') : airlineNames[0]
        const commonItinPayload = {
            a_trip_type: SEARCH_DETAILS?.sectorType === 'DOM' ? 'domestic' : 'international',
            a_journey_type: SEARCH_DETAILS?.journeyType === 'ONE_WAY' ? 'ow' : 'rt',
            a_utm_source: SEARCH_DETAILS?.utmSource || 'organic',
            a_origin: flights?.[0]?.departureAirportCode || 'NA',
            a_itinerary_id: itineraryId,
            a_destination: flights?.[0]?.arrivalAirportCode || 'NA',
            a_airline: airlineNameValue,
            a_flight_number: flightNumbersValue,
            a_ff_fare_type: selectedFareCard?.fareCardMessage?.title || 'N/A',
            is_loyalty_eligible: isEmpty(programDetails) ? 'no' : 'yes',
            loyalty_fare_type: programDetails?.[0]?.programName || 'N/A',
            isAmendment: window.location.pathname == '/flights/amendments' ? true : false,
            a_origin_city: flights?.[0]?.fromCity || flights?.[0]?.departureAirportCode || 'n/a',
            a_destination_city: flights?.[0]?.toCity || flights?.[0]?.arrivalAirportCode || 'n/a',
            a_visa_protection_av: (isEmpty(VISA_REJECTION_COVER) || VISA_REJECTION_COVER?.widgetState === 'DISABLED') ? 'no' : 'yes'
        }
        let finalPayload = { ...commonItinPayload }
        if (
            eventName === 'a_continue_payment_click' ||
            eventName === 'a_itinerary_continue_click' ||
            eventName === 'a_traveller_continue_clicked' ||
            eventName === 'a_add_on_continue_click'
        ) {
            finalPayload = { ...finalPayload, ...extraRavenPayload(itineraryData) }
        }
        if (
            eventName === 'a_itinerary_pageload' ||
            eventName === 'a_itinerary_continue_click' ||
            additionpayload.action_name === 'supercoin_toggle_on_click' ||
            additionpayload.action_name === 'supercoin_toggle_off_click'
        ) {
            finalPayload = { ...finalPayload, ...superCoinPayload(itineraryData) }
        }

        ravenSDKTrigger(eventName, { ...additionpayload, ...finalPayload })
    }
}

export const ravenSDKItinPageLoad = (eventName: string, itineraryData: any, additionpayload: any) => {
    const {
        SEARCH_DETAILS = {},
        FLIGHT: { flights = {} } = {},
        FARE_CARDS: { fareCards = {} } = {}
    } = itineraryData || {}
    const adultCount = SEARCH_DETAILS?.paxInfo?.adults || 0
    const childCount = SEARCH_DETAILS?.paxInfo?.children || 0
    const infantCount = SEARCH_DETAILS?.paxInfo?.infants || 0
    if (flights) {
        const depDate = flights?.[0]?.segments?.[0]?.departDateTime || ''
        const returnDate = flights?.[1]?.segments?.[0]?.departDateTime || ''
        const selectedFareCard = fareCards?.filter?.((fareCard: any) => {
            return fareCard.selected === true
        })?.[0]
        const pageLoadPayLoad = {
            a_search_id: SEARCH_DETAILS?.searchId || 'N/A',
            a_return_date: returnDate
                ? formatDateStandardized(getFormattedDate(returnDate, 'DD/MM/YYYY', 'YYYY/MM/DD'))
                : 'N/A',
            a_stop_count_onward_return:
                flights?.[1]?.stopCount || flights?.[1]?.stopCount === 0
                    ? flights?.[0]?.stopCount + ',' + flights?.[1]?.stopCount
                    : flights?.[0]?.stopCount + '',
            a_dx: dayDiff(
                new Date(
                    getFormattedDate(flights?.[0]?.segments?.[0]?.departDateTime || '', 'MM/DD/YYYY', 'YYYY/MM/DD')
                ),
                new Date()
            ),
            a_adult_count: adultCount,
            a_fare_type: SEARCH_DETAILS?.fareType || 'N/A',
            a_child_count: childCount,
            a_infant_count: infantCount,
            a_departure_date: depDate
                ? formatDateStandardized(getFormattedDate(depDate, 'DD/MM/YYYY', 'YYYY/MM/DD'))
                : 'N/A'
        }
        let finalPayload = { ...pageLoadPayLoad }
        if (eventName === 'a_flight_travellers_review_pageload' || eventName === 'a_traveller_pageload') {
            finalPayload = { ...finalPayload, ...extraRavenPayload(itineraryData) }
        }
        ravenSDKItinTrigger(eventName, itineraryData, { ...finalPayload, ...additionpayload })
    }
}

export const getSftAttribute = (type: string, isIntl: boolean, isRT: boolean, resultsData: any) => {
    if (!isIntl || type === 'regular') {
        return type
    }
    const { onwardLeg = {}, returnLeg = {} } = resultsData?.filterData || {}
    const ptcFareAvailability = (leg: any) => (leg?.ptcFare ? 'available' : 'unavailable')

    const parts = [type, `ow:${ptcFareAvailability(onwardLeg)}`]
    if (isRT) {
        parts.push(`rt:${ptcFareAvailability(returnLeg)}`)
    }
    return parts.join(' | ')
}

export const ravenSDKSrpCfwTrigger = (
    eventName: string,
    resultsData: SearchData,
    searchQuery: Partial<ProcessedData>,
    additionpayload: Record<string, string | number>
) => {
    const search = window.location.search || '';
    let lastPageName = 'home_page'; 
    const sftFareType = getSftAttribute(searchQuery?.sft || 'regular', searchQuery?.isIntl || false,searchQuery?.returnDate ? true : false, resultsData)

    if (search.includes('mevc=y')) {
        lastPageName = 'alternate_dates_flights_click';
    } 
    else if (search.includes('utm_source=')) {
        const utmSource = search.split('utm_source=')[1]?.split('&')[0];
        lastPageName = utmSource || 'home_page';
    } 
    else if (search.includes('dealId=')) {
        lastPageName = 'dealszone';
    }
    const payload = {
        a_utm_source:
            location?.search?.split?.('utm_source=').length > 1
                ? location?.search?.split?.('utm_source=')?.pop()?.split('&')?.[0]
                : getCookie('utm_source') || 'organic',
        last_page_name: lastPageName,
        a_search_id: resultsData?.searchId || 'N/A',
        a_trip_type: (searchQuery?.isIntl || searchQuery?.intl === 'y') ? 'international' : 'domestic',
        a_journey_type: searchQuery?.returnDate || searchQuery?.return_date ? 'rt' : 'ow',
        a_return_date: searchQuery?.returnDate || 'N/A',
        a_dx: dayDiff(
            new Date(getFormattedDate(searchQuery?.departDate || searchQuery?.depart_date || '', 'MM/DD/YYYY', 'DD/MM/YYYY')),
            new Date()
        ),
        a_adult_count: searchQuery?.travellers?.adults || searchQuery?.adults || 0,
        a_origin: searchQuery?.source || 'N/A',
        a_result_count:
            (resultsData?.cards?.J1 ? resultsData?.cards?.J1?.length : 0) +
            (resultsData?.cards?.J2 ? resultsData?.cards?.J2?.length : 0),
        a_child_count: searchQuery?.travellers?.children || searchQuery?.childs || 0,
        a_infant_count: searchQuery?.travellers?.infants || searchQuery?.infants || 0,
        a_departure_date: searchQuery?.departDate || searchQuery?.depart_date || 'N/A',
        a_destination: searchQuery?.destination || 'N/A',
        isAmendment: window.location.pathname == '/flights/amendments' ? true : false,
        is_cfw_user: searchQuery?.isCfw === true ? 'yes' : 'no',
        is_loyalty_eligible: '',
        a_is_sort_applied: 'yes',
        a_is_filter_applied: '',
        a_min_price: resultsData?.filterData?.oneWayPrice?.min|| 'N/A',
        page_name: RAVEN_PAGE_NAME.FLIGHTS_SRP,
        a_fare_type: sftFareType
    }
   
    ravenSDKTrigger(eventName, { ...payload, ...additionpayload })
}

export const ravenSDKRevisedDiscountTrigger = (eventName: string, itineraryData: any, additionpayload: any) => {
    const {
        SEARCH_DETAILS: { sectorType = '' } = {},
        FARE_BREAKUP: { pricingElements = {} } = {}
    } = itineraryData || {};
    const payload = {
        a_fare_price: getBasePlusTax(pricingElements) || 0,
        page_name: 'air_itinerary',
        a_trip_type: sectorType === 'DOM' ? 'domestic' : 'international',
    };
    ravenSDKTrigger(eventName, {...payload, ...additionpayload, ...superCoinPayload(itineraryData)});
}


export const ravenSDKSrpCouponTrigger = (
    eventName: string,
    resultsData: SearchData,
    searchQuery: Partial<ProcessedData>,
    additionpayload: Record<string, string | number>
) => {
    const payload = {
        a_search_id: resultsData?.searchId || 'N/A',
        a_trip_type: (searchQuery?.isIntl || searchQuery?.intl === 'y') ? 'international' : 'domestic',
        a_journey_type: searchQuery?.returnDate || searchQuery?.return_date ? 'rt' : 'ow',
        a_return_date: searchQuery?.returnDate || 'N/A',
        a_dx: Math.max(0, dayDiff(
            new Date(getFormattedDate(searchQuery?.returnDate || searchQuery?.return_date || '', 'MM/DD/YYYY', 'DD/MM/YYYY')),
            new Date(getFormattedDate(searchQuery?.departDate || searchQuery?.depart_date || '', 'MM/DD/YYYY', 'DD/MM/YYYY'))
        )),
        a_origin: searchQuery?.source || 'N/A',
        a_departure_date: searchQuery?.departDate || searchQuery?.depart_date || 'N/A',
        a_destination: searchQuery?.destination || 'N/A',
        is_cfw_user: searchQuery?.isCfw === true ? 'yes' : 'no',
        page_name: RAVEN_PAGE_NAME.FLIGHTS_SRP
    }
    ravenSDKTrigger(eventName, { ...payload, ...additionpayload })
}

export const ravenSDKHomeTrigger = (
    eventName: string,
    payload: Record<string, unknown> = {}
) => {
   try {
    const currentPath = !isServer() ? window?.location?.pathname : '';
    let aPageName = RAVEN_PAGE_NAME.CLEARTRIP_HOME;
    if (currentPath === '/flights') {
        aPageName = RAVEN_PAGE_NAME.FLIGHTS_HOME;
    }

    const lastPageName = location?.search?.split?.('utm_source=').length > 1
        ? location?.search?.split?.('utm_source=')?.pop()?.split('&')?.[0]
        : '';

    const finalPayload = {
        [RAVEN_COMMON_KEYS.PAGE_NAME]: aPageName,
        last_page_name: lastPageName,
        ...payload
    };

    ravenSDKTrigger(eventName, finalPayload);
    } catch (error) {
        console.error('Error triggering Raven event:', error)
    }
};

export const ravenSDKConfirmationPageLoad = ({ eventName, itineraryData, initialTS, ...additionpayload }: {
    eventName: string
    itineraryData: any
    initialTS?: number
    [key: string]: any
}) => {
    const {
        flightDetails,
        itineraryTags,
        itineraryId,
        trackingDetails = {},
        utmSource,
        searchCriteria = {}
    } = itineraryData || {}
    const {
        international,
        arrivalDate,
        from,
        to,
        tripId,
        departDate,
        paxInfo = {},
        sft
    } = searchCriteria
    const { adults, children, infants } = paxInfo

    const flightNumbers: string[] = []
    const airlineNames: string[] = []
    if (flightDetails) {
        flightDetails.forEach((flight: any) =>
            flight.segments.forEach((segment: any) => {
                flightNumbers.push(segment.airline + '-' + segment.flightNumber)
                airlineNames.push(segment.airlineName)
            })
        )
        const flightNumbersValue = flightNumbers.length > 1 ? flightNumbers.join(',') : flightNumbers[0]
        const airlineNameValue = airlineNames.length > 1 ? airlineNames.join(',') : airlineNames[0]

        const pageLoadPayLoad = {
            is_cfw_user: isCfwTagPresent({ SEARCH_DETAILS: { itineraryTags } }) ? 'yes' : 'no',
            a_trip_type: international ? 'international' : 'domestic',
            a_journey_type: arrivalDate ? 'rt' : 'ow',
            a_origin: from || 'N/A',
            a_itinerary_id: itineraryId || 'N/A',
            a_destination: to || 'N/A',
            a_airline: airlineNameValue,
            a_flight_number: flightNumbersValue,
            a_utm_source: trackingDetails?.utmSource || utmSource || 'organic',
            page_loadtime: initialTS ? new Date().getTime() - initialTS : 'N/A',
            a_search_id: tripId || 'N/A',
            a_return_date: arrivalDate
                ? getFormattedDate(arrivalDate, 'DD/MM/YYYY', 'YYYY-MM-DDTHH:mm:ssZ')
                : 'N/A',
            a_stop_count_onward_return:
                flightDetails?.[1]?.stopCount || flightDetails?.[1]?.stopCount === 0
                    ? flightDetails?.[0]?.stopCount + ',' + flightDetails?.[1]?.stopCount
                    : flightDetails?.[0]?.stopCount + '',
            a_dx: dayDiff(
                new Date(getFormattedDate(departDate || '', 'MM/DD/YYYY', 'YYYY-MM-DDTHH:mm:ssZ')),
                new Date()
            ),
            a_adult_count: adults || 0,
            a_fare_type: sft || 'regular',
            a_child_count: children || 0,
            a_infant_count: infants || 0,
            a_departure_date: departDate
                ? getFormattedDate(departDate, 'DD/MM/YYYY', 'YYYY-MM-DDTHH:mm:ssZ')
                : 'N/A'
        }
        ravenSDKTrigger(eventName, { ...pageLoadPayLoad, ...additionpayload })
    }
}

export const generateSrpAdditionalPayload = ({
    action_name='',
    action_type='',
    property='',
    listOfFares,
    index,
    card,
    stops,
    searchData,
    isSpecialRT,
    legNo
}: {
    action_name: string
    action_type: string
    property: string
    listOfFares: any
    index: number
    card: any
    stops: number
    searchData: any
    isSpecialRT: boolean
    legNo: number
}) =>{
    try {
        const { metaData = {}, filterData = {} } = searchData || {}
        const { onwardLeg = {}, returnLeg = {} } = filterData || {}
        const { summary = {} } = card || {}
        const { flights = [], lastArrival = {} } = summary || {}
        const flightNumberValue: string[] = []
        const airlineNamesValue: string[] = []
        const { displayPrice } = listOfFares?.[0] || {}
        const farePrice = listOfFares?.[0]?.pricing?.totalPricing?.totalPrice || displayPrice
        flights.map((flight: any) => {
            flightNumberValue.push(`${flight.airlineCode}-${flight.flightNumber}`)
            airlineNamesValue.push(metaData.airlineDetail?.[flight.airlineCode]?.name)
        })
        const ptcTagData = getPtcTagData(listOfFares?.[0]) || {}
        const tuppleType = !isEmpty(ptcTagData) ? PTC_TAG_TYPE[ptcTagData.benefitType as keyof typeof PTC_TAG_TYPE] : 'N/A'
        return {
            action_name,
            action_type,
            page_name: 'flights_srp',
            property,
            a_flight_tuple_num: index + 1 || 'N/A',
            a_flight_number: flightNumberValue.join(',') || 'N/A',
            a_stop_count_onward_return: stops || 0,
            a_airline: airlineNamesValue.join(',') || 'N/A',
            available_fares: listOfFares?.length || 0,
            tuple_num: index + 1 || 'N/A',
            a_nearby_airport: lastArrival?.airport?.code || 'N/A',
            spl_rt_flight: isSpecialRT ? 'yes' : 'no',
            a_fare_price: displayPrice || farePrice || 'N/A',
            a_min_price: legNo === 0 ? onwardLeg?.oneWayPrice?.min || 'N/A' : returnLeg?.oneWayPrice?.min || 'N/A',
            a_tupple_type: tuppleType
        }
    } catch (error) {
        console.error('Error generating SRP additional payload:', error)
    }
}
